/* eslint-disable react-refresh/only-export-components */
import type { ToolResultCard } from "@/api/chat";
import { Badge } from "@/components/ui/badge";
import {
  ActionIcon,
  CompactActionLabel,
  type ActionData,
} from "@/components/workflows/action-display";
import {
  CircleDotIcon,
  ClipboardCheckIcon,
  PlayCircleIcon,
  SkipForwardIcon,
} from "lucide-react";

// ─── Type guards ─────────────────────────────────────────────

export function isToolResultCard(
  result: unknown,
): result is ToolResultCard {
  return (
    typeof result === "object" &&
    result !== null &&
    "card_type" in result &&
    "data" in result
  );
}

// ─── Card data types ─────────────────────────────────────────

type WorkflowPreviewData = {
  name: string;
  is_enabled: boolean;
  project_field_name: string | null;
  field_values: string[];
  actions: ActionData[];
  match_count: number | null;
};

type DryRunProject = {
  name: string;
  id: string;
  status: "run" | "skip";
};

type DryRunResultsData = {
  workflow_name: string;
  is_enabled: boolean;
  total_matching: number;
  would_run: number;
  would_skip: number;
  projects: DryRunProject[];
};

type OpenItemChangePreviewData = {
  items: Array<{
    id: string;
    title: string;
    changes: Record<string, { from: unknown; to: unknown }>;
  }>;
  expires_in_minutes: number;
};

// ─── Card registry ───────────────────────────────────────────

const CARD_COMPONENTS: Record<
  string,
  React.FC<{ data: Record<string, unknown> }>
> = {
  workflow_preview: WorkflowPreviewCard,
  dry_run_results: DryRunResultsCard,
  open_item_change_preview: OpenItemChangePreviewCard,
};

export function ToolCard({ card }: { card: ToolResultCard }) {
  const Component = CARD_COMPONENTS[card.card_type];
  if (!Component) return null;
  return <Component data={card.data} />;
}

// ─── Workflow Preview Card ───────────────────────────────────

function WorkflowPreviewCard({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const {
    name,
    is_enabled: isEnabled,
    project_field_name: projectFieldName,
    field_values: fieldValues = [],
    actions = [],
    match_count: matchCount,
  } = data as unknown as WorkflowPreviewData;

  return (
    <div className="mt-2 rounded-lg border bg-background/80 p-3 text-xs">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-foreground">{name}</span>
        <Badge
          variant={isEnabled ? "default" : "secondary"}
          className="h-5 text-[10px]"
        >
          {isEnabled ? "Active" : "Draft"}
        </Badge>
      </div>

      {/* Field filter */}
      <div className="mb-2 flex flex-wrap items-center gap-1">
        {projectFieldName && fieldValues.length > 0 ? (
          <>
            <span className="text-[10px] font-medium text-muted-foreground">
              {projectFieldName}:
            </span>
            {fieldValues.map((val) => (
              <Badge
                key={val}
                variant="outline"
                className="h-5 text-[10px] font-normal"
              >
                {val.replace(/_/g, " ")}
              </Badge>
            ))}
          </>
        ) : (
          <Badge variant="outline" className="h-5 text-[10px] font-normal">
            All projects
          </Badge>
        )}
      </div>

      {/* Action pipeline */}
      {actions.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {actions.map((action, i) => (
            <div key={i} className="flex items-center gap-1">
              {i > 0 && (
                <span className="text-muted-foreground/50">&rarr;</span>
              )}
              <div className="inline-flex items-center gap-1 rounded border bg-muted/50 px-1.5 py-0.5">
                <ActionIcon type={action.type} className="h-3 w-3 flex-shrink-0" />
                <CompactActionLabel action={action} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Match count */}
      {matchCount !== null && matchCount !== undefined && (
        <div className="mt-2 flex items-center gap-1 text-muted-foreground">
          <CircleDotIcon className="h-3 w-3" />
          <span>
            Matches <strong className="text-foreground">{matchCount}</strong>{" "}
            project{matchCount !== 1 ? "s" : ""}
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Dry Run Results Card ────────────────────────────────────

function DryRunResultsCard({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const {
    workflow_name: workflowName,
    is_enabled: isEnabled,
    total_matching: totalMatching,
    would_run: wouldRun,
    would_skip: wouldSkip,
    projects = [],
  } = data as unknown as DryRunResultsData;

  return (
    <div className="mt-2 rounded-lg border bg-background/80 p-3 text-xs">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold text-foreground">
          Dry Run: {workflowName}
        </span>
        <Badge
          variant={isEnabled ? "default" : "secondary"}
          className="h-5 text-[10px]"
        >
          {isEnabled ? "Active" : "Draft"}
        </Badge>
      </div>

      {/* Summary stats */}
      <div className="mb-2 flex gap-4">
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-blue-500" />
          <span>{totalMatching} matching</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <span>{wouldRun} would run</span>
        </div>
        {wouldSkip > 0 && (
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-gray-400" />
            <span>{wouldSkip} would skip</span>
          </div>
        )}
      </div>

      {/* Project list */}
      {projects.length > 0 && (
        <div className="max-h-40 space-y-1 overflow-y-auto">
          {projects.map((project) => (
            <div
              key={project.id}
              className="flex items-center justify-between rounded px-2 py-1 odd:bg-muted/30"
            >
              <span className="truncate">{project.name}</span>
              {project.status === "run" ? (
                <span className="flex items-center gap-1 text-green-600">
                  <PlayCircleIcon className="h-3 w-3" />
                  Run
                </span>
              ) : (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <SkipForwardIcon className="h-3 w-3" />
                  Skip
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {totalMatching > projects.length && (
        <div className="mt-1 text-center text-muted-foreground">
          + {totalMatching - projects.length} more project
          {totalMatching - projects.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}

function displayChangeValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "None";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function OpenItemChangePreviewCard({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const { items = [], expires_in_minutes: expiresInMinutes } =
    data as unknown as OpenItemChangePreviewData;
  const changedCount = items.reduce(
    (total, item) => total + Object.keys(item.changes).length,
    0,
  );

  return (
    <div className="mt-2 rounded-lg border bg-background/90 p-3 text-xs text-foreground">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 font-semibold">
          <ClipboardCheckIcon className="h-3.5 w-3.5" /> Open Item changes
        </span>
        <Badge variant="outline" className="h-5 text-[10px]">
          Preview only
        </Badge>
      </div>
      <p className="mb-3 text-muted-foreground">
        {changedCount} field change{changedCount === 1 ? "" : "s"} across{" "}
        {items.length} item{items.length === 1 ? "" : "s"}. Nothing has been
        applied.
      </p>
      <div className="max-h-64 space-y-2 overflow-y-auto">
        {items.map((item) => (
          <div key={item.id} className="rounded border bg-muted/20 p-2">
            <p className="mb-1.5 font-medium">{item.title}</p>
            {Object.entries(item.changes).map(([field, change]) => (
              <div key={field} className="grid grid-cols-[6rem_1fr] gap-2 py-0.5">
                <span className="capitalize text-muted-foreground">
                  {field.replace(/_/g, " ")}
                </span>
                <span className="min-w-0 break-words">
                  <span className="line-through opacity-60">
                    {displayChangeValue(change.from)}
                  </span>{" "}
                  → {displayChangeValue(change.to)}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
      <p className="mt-2 text-[10px] text-muted-foreground">
        Confirm in chat within {expiresInMinutes} minutes to apply atomically.
      </p>
    </div>
  );
}
